import moment from "moment-timezone";
import { Calendar, Download } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { TimezoneOption } from "@core/features/timezones";
import {
  createGoogleCalendarUrl,
  generateICSContent,
} from "@core/features/calendar";
import type { SelectableOption } from "@core/features/actions";

export type Props = {
  /** The selectable option representing a scheduled time */
  option: SelectableOption;
  /** Title for the calendar event */
  eventTitle: string;
  /** Description for the calendar event */
  eventDescription?: string;
  /** User's timezone for display */
  userTimezone?: TimezoneOption;
  /** Callback when calendar event is added */
  onAddCalendarEvent: (
    eventId: number | string | "all",
    calendarType: "google" | "ical"
  ) => void;
};

/**
 * Convert a SelectableOption to ISO date-time strings
 */
function optionToDateTimes(option: SelectableOption): {
  startDateTime: string;
  endDateTime: string;
} {
  const startDateTime = moment
    .utc(option.date)
    .hour(option.startHour)
    .minute(option.startMinute)
    .second(0)
    .toISOString();

  const endDateTime = moment
    .utc(option.date)
    .hour(option.endHour)
    .minute(option.endMinute)
    .second(0)
    .toISOString();

  return { startDateTime, endDateTime };
}

/**
 * Format the option time for display in user's timezone
 */
function formatOptionTime(
  option: SelectableOption,
  timezone: TimezoneOption
): string {
  const localStart = moment
    .utc(option.date)
    .hour(option.startHour)
    .minute(option.startMinute)
    .tz(timezone.name);

  const localEnd = moment
    .utc(option.date)
    .hour(option.endHour)
    .minute(option.endMinute)
    .tz(timezone.name);

  // Format: "Friday, January 17, 2026 at 9:00am - 11:00am EST"
  const dateStr = localStart.format("dddd, MMMM D, YYYY");
  const startTimeStr =
    localStart.minute() > 0
      ? localStart.format("h:mma")
      : localStart.format("ha");
  const endTimeStr =
    localEnd.minute() > 0 ? localEnd.format("h:mma") : localEnd.format("ha");
  const tzAbbr = timezone.abbreviation || localStart.format("z");

  return `${dateStr} at ${startTimeStr} - ${endTimeStr} ${tzAbbr}`;
}

/**
 * Renders a calendar event item from a SelectableOption (scheduled time).
 * Provides Google Calendar and iCal download buttons.
 */
export function CalendarEventTimeItem({
  option,
  eventTitle,
  eventDescription = "Your scheduled peer circle meeting time.",
  userTimezone,
  onAddCalendarEvent,
}: Props) {
  if (!userTimezone) return null;

  const { startDateTime, endDateTime } = optionToDateTimes(option);
  const formattedTime = formatOptionTime(option, userTimezone);
  const optionId = option.id || `${option.date}-${option.startHour}`;

  const handleGoogleCalendarClick = () => {
    const url = createGoogleCalendarUrl({
      startDateTime,
      endDateTime,
      eventTitle,
      eventDescription,
    });
    window.open(url, "_blank");
    onAddCalendarEvent(optionId, "google");
  };

  const handleICalClick = () => {
    const icsContent = generateICSContent(
      [{ id: optionId, start_time: startDateTime, end_time: endDateTime }],
      { eventTitle, eventDescription }
    );

    const blob = new Blob([icsContent], {
      type: "text/calendar;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${eventTitle.replace(/\s+/g, "_")}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    onAddCalendarEvent(optionId, "ical");
  };

  return (
    <div className="flex flex-col items-start md:flex-row md:items-center justify-between gap-4">
      <div className="flex flex-col gap-1 flex-1">
        <Type variant="h6" as="h3">{eventTitle}</Type>
        <Type variant="caption">{formattedTime}</Type>
      </div>
      <div className="flex flex-row gap-2 flex-shrink-0">
        <Button
          variant="secondary"
          iconComponent={Download}
          onClick={handleICalClick}
          aria-label="Download iCal file"
        >
          iCal
        </Button>
        <Button
          variant="secondary"
          iconComponent={Calendar}
          onClick={handleGoogleCalendarClick}
          aria-label="Add to Google Calendar"
        >
          Google
        </Button>
      </div>
    </div>
  );
}

export default CalendarEventTimeItem;
